import React from 'react';
import { motion } from 'framer-motion';
export interface ReceiptItem {
  name: string;
  price: number;
  quantity?: number;
}
export interface ReceiptVisualProps {
  items: ReceiptItem[];
  storeName?: string;
  showTotal?: boolean;
  totalBudget?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
  /** Optional unit for the price, e.g. 'st', 'kg', 'liter'. Defaults to 'st' */
  priceUnit?: string;
}
function formatEuro(cents: number): string {
  const euros = cents / 100;
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(euros);
}
function formatEuroFromEuros(euros: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(euros);
}
/** Detect whether prices are in cents (>10 and integer) or euros */
function isCents(items: ReceiptItem[]): boolean {
  return items.every(i => Number.isInteger(i.price) && Math.abs(i.price) >= 10);
}
export function ReceiptVisual({
  items,
  storeName,
  showTotal = true,
  totalBudget,
  label,
  mode = 'support',
  priceUnit = 'st'
}: ReceiptVisualProps) {
  const validItems = (items || []).filter(i => i.name && typeof i.price === 'number' && Number.isFinite(i.price));
  if (validItems.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13622" data-mp="2glo9fl0bw4qb">
        <p className="text-red-600 text-sm font-medium" data-id="element-13623" data-mp="2ckmbd7i5dypx">
          Geen producten op de kassabon
        </p>
      </div>;
  }
  const useCents = isCents(validItems);
  const fmt = useCents ? formatEuro : formatEuroFromEuros;
  const lineItems = validItems.map(item => {
    const qty = item.quantity ?? 1;
    const lineTotal = item.price * qty;
    return {
      ...item,
      quantity: qty,
      lineTotal
    };
  });
  const subtotal = lineItems.reduce((sum, i) => sum + i.lineTotal, 0);
  const budgetVal = totalBudget ?? 0;
  const change = budgetVal > 0 ? budgetVal - subtotal : 0;
  // ─── Stimulus mode: compact receipt ──────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-amber-50/80 rounded-xl border border-amber-200 overflow-hidden max-w-xs mx-auto" data-id="element-13624" data-mp="35oqbk7ibczqg">
        {/* Header */}
        <div className="bg-amber-100 px-4 py-2.5 border-b border-amber-200 text-center" data-id="element-13625" data-mp="39nz6vgt055o4">
          <span className="text-xs font-bold text-amber-900 tracking-wide uppercase" data-id="element-13626" data-mp="2vozepz6a68n7">
            {storeName || '🧾 Kassabon'}
          </span>
        </div>

        {/* Items */}
        <div className="px-4 py-3 space-y-1.5" data-id="element-13627" data-mp="2mpugktymshbk">
          {lineItems.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: i * 0.06,
          duration: 0.25
        }} className="flex items-baseline justify-between gap-2 text-sm" data-id="element-13628" data-mp="2qpwbmk9v2chv" data-mpi={i}>
              <div className="flex items-baseline gap-1.5 min-w-0" data-id="element-13629" data-mp="25hbjry687o4q">
                <span className="text-amber-800 truncate" data-id="element-13630" data-mp="2j7ewnyj7u5hi">{item.name}</span>
                {item.quantity > 1 && <span className="text-amber-500 text-xs flex-shrink-0" data-id="element-13631" data-mp="2efn5pmw5dxqu">
                    ×{item.quantity}
                  </span>}
              </div>
              <span className="text-amber-900 font-mono text-xs flex-shrink-0 tabular-nums" data-id="element-13632" data-mp="2o6rkomltpilo">
                {fmt(item.lineTotal)}
              </span>
            </motion.div>)}
        </div>

        {/* Total */}
        {showTotal && <div className="border-t border-dashed border-amber-300 px-4 py-2.5 flex justify-between items-baseline" data-id="element-13633" data-mp="2x7pnj1kizk2o">
            <span className="text-sm font-bold text-amber-900" data-id="element-13634" data-mp="17tt9uk0twfz5">Totaal</span>
            <span className="text-sm font-bold font-mono text-amber-900 tabular-nums" data-id="element-13635" data-mp="12uq570orgbrp">
              {fmt(subtotal)}
            </span>
          </div>}

        {label && <div className="px-4 pb-2.5" data-id="element-13636" data-mp="197ua67phrycw">
            <p className="text-xs text-amber-600 text-center" data-id="element-13637" data-mp="19azz9my4f08m">{label}</p>
          </div>}
      </div>;
  }
  // ─── Support mode: educational receipt with breakdown ────────────────
  return <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 shadow-sm overflow-hidden max-w-sm mx-auto" data-id="element-13638" data-mp="ljkw5ryisewb">
      {/* Store header */}
      <div className="bg-amber-100 px-5 py-3 border-b border-amber-200 text-center" data-id="element-13639" data-mp="qixkkh51hu10">
        <div className="text-lg font-bold text-amber-900" data-id="element-13640" data-mp="vg63ijy0i23b">
          {storeName || '🧾 Kassabon'}
        </div>
        {label && <p className="text-xs text-amber-600 mt-0.5" data-id="element-13641" data-mp="10ajy4k4uo56x">{label}</p>}
      </div>

      {/* Receipt body */}
      <div className="px-5 py-4" data-id="element-13642" data-mp="2r8ul7foxhn8">
        {/* Line items */}
        <div className="space-y-2 mb-4" data-id="element-13643" data-mp="7qv6ndjs8rug">
          {lineItems.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.08,
          duration: 0.3
        }} className="flex items-center justify-between gap-3" data-id="element-13644" data-mp="7lytoqxd17i" data-mpi={i}>
              <div className="flex items-center gap-2 min-w-0" data-id="element-13645" data-mp="3dgqedijttp5x">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" data-id="element-13646" data-mp="38nxbz1btysx0" />
                <span className="text-sm text-amber-900 truncate" data-id="element-13647" data-mp="33p43xofbbu48">
                  {item.name}
                </span>
                {item.quantity > 1 && <span className="text-xs text-amber-500 flex-shrink-0" data-id="element-13648" data-mp="2ys3wb92tejb0">
                    ×{item.quantity}
                  </span>}
              </div>
              <div className="flex items-baseline gap-2 flex-shrink-0" data-id="element-13649" data-mp="2pqw2p4b8u6n9">
                {item.quantity > 1 && <span className="text-xs text-amber-400" data-id="element-13650" data-mp="2f2xv8v6lokx4">
                    {fmt(item.price)}/{priceUnit}
                  </span>}
                <span className="text-sm font-semibold font-mono text-amber-900 tabular-nums" data-id="element-13651" data-mp="264cdv3cv7bxx">
                  {fmt(item.lineTotal)}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Dashed separator */}
        <div className="border-t-2 border-dashed border-amber-300 my-3" data-id="element-13652" data-mp="2yukyd1uvi8fo" />

        {/* Subtotal */}
        {showTotal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: lineItems.length * 0.08 + 0.1
      }} className="flex justify-between items-baseline mb-2" data-id="element-13653" data-mp="2p2orb1at77zb">
            <span className="text-sm font-bold text-amber-900" data-id="element-13654" data-mp="3miax68nkxhlx">Totaal</span>
            <span className="text-base font-bold font-mono text-amber-900 tabular-nums" data-id="element-13655" data-mp="3vwuro72yfqin">
              {fmt(subtotal)}
            </span>
          </motion.div>}

        {/* Budget & change */}
        {budgetVal > 0 && <>
            <div className="flex justify-between items-baseline text-sm text-amber-700 mb-1" data-id="element-13656" data-mp="9fyrfm53pgtu">
              <span data-id="element-13657" data-mp="4ji2ipq2uyfp">Betaald</span>
              <span className="font-mono tabular-nums" data-id="element-13658" data-mp="4k9wjw410o3v">{fmt(budgetVal)}</span>
            </div>
            <div className={`flex justify-between items-baseline text-sm font-bold ${change >= 0 ? 'text-emerald-700' : 'text-amber-900'}`} data-id="element-13659" data-mp="3d033bj8k2l7q">
              <span data-id="element-13660" data-mp="3mwzfbyxnxph1">{change >= 0 ? 'Wisselgeld' : 'Tekort'}</span>
              <span className="font-mono tabular-nums" data-id="element-13661" data-mp="3hwk2r9l7gm04">
                {fmt(Math.abs(change))}
              </span>
            </div>
          </>}
      </div>

      {/* Educational tip */}
      <div className="bg-white/70 px-5 py-3 border-t border-amber-100" data-id="element-13662" data-mp="x74dij1mx5cl">
        <div className="flex items-start gap-2.5" data-id="element-13663" data-mp="x98hf0zja4cv">
          <span className="text-amber-500 text-sm mt-0.5" data-id="element-13664" data-mp="2ro457dt4z3hv">💡</span>
          <p className="text-xs text-amber-800 leading-relaxed" data-id="element-13665" data-mp="2voen9ou7dkd3">
            <strong data-id="element-13666" data-mp="2vmlf253xys2z">Tip:</strong> Tel de euro's en centen apart op. Komen de
            centen boven de 100? Dan gaat er 1 euro over.
          </p>
        </div>
      </div>
    </div>;
}